import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import LogoLoader from '@/components/LogoLoader';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/date';

const TimeEntries = () => {
  const { token } = useAuth();
  const [timeEntries, setTimeEntries] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pos, setPos] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    consultant_id: '',
    client_id: '',
    project_id: '',
    po_id: '',
    week_start: '',
    hours: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [entriesData, consultantsData, clientsData, projectsData, posData] = await Promise.all([
        api.get('/time-entries', token),
        api.get('/consultants', token),
        api.get('/clients', token),
        api.get('/projects', token),
        api.get('/purchase-orders', token)
      ]);
      setTimeEntries(entriesData);
      setConsultants(consultantsData);
      setClients(clientsData);
      setProjects(projectsData);
      setPos(posData);
    } catch (error) {
      toast.error('Failed to load time entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate: must have either project_id or po_id, but not both
    if (!formData.project_id && !formData.po_id) {
      toast.error('Please select either a Project or a Purchase Order');
      return;
    }
    if (formData.project_id && formData.po_id) {
      toast.error('Please select only one: either Project or Purchase Order, not both');
      return;
    }
    
    try {
      const payload = {
        consultant_id: formData.consultant_id,
        week_start: formData.week_start,
        hours: parseFloat(formData.hours),
        notes: formData.notes || null
      };
      
      // Add only the selected one (project or PO)
      if (formData.project_id) {
        payload.project_id = formData.project_id;
      } else if (formData.po_id) {
        payload.po_id = formData.po_id;
      }
      
      await api.post('/time-entries', payload, token);
      toast.success('Time entry created successfully');
      setShowDialog(false);
      loadData();
      setFormData({ consultant_id: '', client_id: '', project_id: '', po_id: '', week_start: '', hours: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create time entry');
    }
  };

  const handleApproval = async (status) => {
    if (selectedEntries.length === 0) {
      toast.error('Please select time entries to approve/reject');
      return;
    }

    try {
      await api.post('/time-entries/approve', {
        time_entry_ids: selectedEntries,
        approval_status: status
      }, token);
      toast.success(`${selectedEntries.length} entries ${status.toLowerCase()}`);
      setSelectedEntries([]);
      loadData();
    } catch (error) {
      toast.error('Failed to update approvals');
    }
  };

  const getConsultantName = (id) => consultants.find(c => c.id === id)?.name || 'Unknown';
  const getProjectName = (entry) => {
    if (entry.project?.name) {
      return entry.project.name;
    }
    if (!entry.project_id) {
      return 'Not applicable';
    }
    return projects.find(p => p.id === entry.project_id)?.name || 'Unknown';
  };
  const getPurchaseOrderLabel = (entry) => {
    if (entry?.po?.po_number) return entry.po.po_number;
    if (entry?.po_number) return entry.po_number;
    if (entry?.po_id) return entry.po_id;
    return 'Not linked';
  };

  const pendingEntries = timeEntries.filter(e => e.approval_status === 'Pending');
  const approvedEntries = timeEntries.filter(e => e.approval_status === 'Approved');

  if (loading) {
    return <LogoLoader message="Loading time entries..." />;
  }

  return (
    <div className="space-y-6" data-testid="time-entries-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Time Entries</h1>
          <p className="text-gray-600 mt-1">Manage weekly time submissions</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-utis-forest hover:bg-utis-forest/90 text-white" data-testid="add-time-entry-button">
          <Plus className="w-4 h-4 mr-2" />
          Add Time Entry
        </Button>
      </div>

      {/* Pending Approvals Section */}
      {pendingEntries.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-orange-900">Pending Approvals ({pendingEntries.length})</CardTitle>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  onClick={() => handleApproval('Approved')}
                  className="bg-utis-forest hover:bg-utis-forest/90 text-white"
                  disabled={selectedEntries.length === 0}
                  data-testid="approve-button"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve Selected
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleApproval('Rejected')}
                  disabled={selectedEntries.length === 0}
                  data-testid="reject-button"
                >
                  <X className="w-4 h-4 mr-1" />
                  Reject Selected
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Consultant</TableHead>
                  <TableHead>Purchase Order</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Week Start</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedEntries.includes(entry.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedEntries([...selectedEntries, entry.id]);
                          } else {
                            setSelectedEntries(selectedEntries.filter(id => id !== entry.id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{getConsultantName(entry.consultant_id)}</TableCell>
                    <TableCell>{getPurchaseOrderLabel(entry)}</TableCell>
                    <TableCell>{getProjectName(entry)}</TableCell>
                    <TableCell>{formatDisplayDate(entry.week_start, '-')}</TableCell>
                    <TableCell>{entry.hours} hrs</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Pending</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Approved Entries Section */}
      <Card>
        <CardHeader>
          <CardTitle>Approved Time Entries ({approvedEntries.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {approvedEntries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No approved time entries yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Consultant</TableHead>
                  <TableHead>Purchase Order</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Week Start</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Approved At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {approvedEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">{getConsultantName(entry.consultant_id)}</TableCell>
                    <TableCell>{getPurchaseOrderLabel(entry)}</TableCell>
                    <TableCell>{getProjectName(entry)}</TableCell>
                    <TableCell>{formatDisplayDate(entry.week_start, '-')}</TableCell>
                    <TableCell>{entry.hours} hrs</TableCell>
                    <TableCell>
                      <Badge className="bg-utis-sage/20 text-utis-forest">Approved</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {entry.approved_at ? formatDisplayDateTime(entry.approved_at, '-') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Time Entry Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Time Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Consultant *</Label>
              <Select value={formData.consultant_id} onValueChange={(val) => setFormData({...formData, consultant_id: val})} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select consultant" />
                </SelectTrigger>
                <SelectContent>
                  {consultants.filter(c => c.is_active).map(consultant => (
                    <SelectItem key={consultant.id} value={consultant.id}>{consultant.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Client *</Label>
              <Select 
                value={formData.client_id} 
                onValueChange={(val) => setFormData({...formData, client_id: val, project_id: '', po_id: ''})}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.filter(c => c.is_active).map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Project {!formData.po_id && '*'}</Label>
              <Select 
                value={formData.project_id} 
                onValueChange={(val) => setFormData({...formData, project_id: val, po_id: val ? '' : formData.po_id})}
                disabled={!!formData.po_id || !formData.client_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !formData.client_id ? "Select client first" :
                    formData.po_id ? "Clear PO to select Project" : 
                    "Select project"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {projects.filter(p => p.is_active && p.client_id === formData.client_id).map(project => (
                    <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.project_id && (
                <button
                  type="button"
                  onClick={() => setFormData({...formData, project_id: ''})}
                  className="text-xs text-red-600 hover:underline mt-1"
                >
                  Clear selection
                </button>
              )}
            </div>
            
            <div className="flex items-center justify-center text-sm text-gray-500">
              - OR -
            </div>
            
            <div>
              <Label>Purchase Order {!formData.project_id && '*'}</Label>
              <Select 
                value={formData.po_id} 
                onValueChange={(val) => setFormData({...formData, po_id: val, project_id: val ? '' : formData.project_id})}
                disabled={!!formData.project_id || !formData.client_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !formData.client_id ? "Select client first" :
                    formData.project_id ? "Clear Project to select PO" : 
                    "Select PO"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {pos.filter(po => po.is_active && po.client_id === formData.client_id).map(po => (
                    <SelectItem key={po.id} value={po.id}>{po.po_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.po_id && (
                <button
                  type="button"
                  onClick={() => setFormData({...formData, po_id: ''})}
                  className="text-xs text-red-600 hover:underline mt-1"
                >
                  Clear selection
                </button>
              )}
            </div>
            <div>
              <Label>Week Start (Monday) *</Label>
              <Input type="date" value={formData.week_start} onChange={(e) => setFormData({...formData, week_start: e.target.value})} required />
            </div>
            <div>
              <Label>Hours *</Label>
              <Input type="number" step="0.5" value={formData.hours} onChange={(e) => setFormData({...formData, hours: e.target.value})} required placeholder="40" />
            </div>
            <Button type="submit" className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white">Create Time Entry</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeEntries;
